import {
  Autocomplete,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Box,
  TextField,
} from '@mui/material';
import { useContext } from 'react';
import PropTypes from 'prop-types';

import { COLOR4 } from '../../../constants';
import { CommonStyles } from '../CommonStyles';
import { handleAddressEditModal, isWebComponentFlow } from '../../commonCode';

import LeadGenerationContext from '../../../context/LeadGenerationContext';
import { EditIcon } from '../../../images/svg/CustomizedSVG';

export const CustomAutoComplete = ({
  id,
  dataTestIdAutocomplete,
  required,
  inputLabelAriaLabel,
  InputLabelClassName,
  label,
  options,
  value,
  handleChange,
  handleInputChange,
  filterOptions,
  maxLength,
  autoFocus,
  placeholder,
  fullWidth,
  error,
  InputClassName,
  dataTestIdForLabel,
  dataTestIdForInput,
  bundleAddress,
  isBundle = false,
  disabledAddressBundle,
}) => {
  const baseContext = isWebComponentFlow();
  const {
    isFullAddressEdited,
    setIsFullAddressEdited,
    setAddressInformation,
    theme,
    isLightTheme,
  } = baseContext;
  const classes = CommonStyles(theme);
  const showError = Boolean(error);
  const handleEditClick = (e) => {
    setIsFullAddressEdited(true);
    setAddressInformation((prevState) => ({
      ...prevState,
      editHomeAddress: true,
    }));
    handleAddressEditModal(baseContext);
    // handleChange(e);
  };

  const bundleInput = (params) => (
    <TextField
      multiline
      variant="outlined"
      data-testid={dataTestIdForInput || null}
      inputRef={params.InputProps.ref}
      id={id || null}
      name={id || null}
      autoFocus={autoFocus || false}
      sx={[
        classes.outlined_inputs,
        {
          '&.Mui-focused': {
            border: `1px solid ${theme?.palette?.primary?.main}`,
          },
        },
        InputClassName,
        ...(disabledAddressBundle
          ? [
              {
                ...classes.outlined_input_bundle,
                backgroundColor: theme?.palette?.disabledAddress?.main,
                '& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled': {
                  color: theme?.palette?.disabled?.main,
                  WebkitTextFillColor: theme?.palette?.disabled?.main,
                },
              },
            ]
          : isBundle
          ? [
              classes.customBorderRadius,
              classes.outlined_input_bundle,
              ...(showError ? [{ backgroundColor: '#D5151F1A !important' }] : []),
            ]
          : []),
        {
          height: 'auto !important',
          width: 'auto !important',
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none !important',
            padding: '0px !important',
          },
        },
      ]}
      slotProps={{
        notchedOutline: {
          sx: [
            error ? classes.notchedOutlineErr : classes.notchedOutline,
            ...(disabledAddressBundle
              ? [
                  {
                    border: 'none',
                    '&.Mui-disabled': {
                      backgroundColor: '#E6E6E6',
                    },
                  },
                ]
              : isBundle
              ? [classes.customBorderRadius]
              : []),
          ],
        },
        input: {
          ...params.inputProps,
          sx: [
            ...(isBundle ? [{ padding: '14px 14px 10px 14px !important' }] : []),
            {
              '&::placeholder': { fontSize: '16px' },
              '&:-webkit-autofill': {
                WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                WebkitTextFillColor: !isLightTheme ? '#ffffff !important' : 'inherit !important',
                transition: 'background-color 5000s ease-in-out 0s',
                caretColor: 'inherit',
                borderRadius: 'inherit',
                fontSize: 'inherit',
              },
            },
            {
              padding: '0px !important',
              '& .MuiInputBase-inputMultiline': {
                padding: 0,
                margin: 0,
              },
            },
          ],
          endAdornment: bundleAddress && (
            <InputAdornment position="end">
              <IconButton
                onClick={handleEditClick}
                disabled={isFullAddressEdited}
                aria-label="edit"
                edge="end"
              >
                <EditIcon color={theme?.palette?.orientalPrimary?.main} />
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
      placeholder={placeholder || ''}
      fullWidth={fullWidth || false}
      value={value || ''}
      disabled={bundleAddress && !isFullAddressEdited && value !== ''}
      style={error ? { background: COLOR4 } : {}}
    />
  );

  const oldInput = (params) => (
    <OutlinedInput
      inputProps={{
        ...params.inputProps,
        sx: {
          '&::placeholder': {
            fontSize: '16px',
          },
        },
      }}
      data-testid={dataTestIdForInput || null}
      ref={params.InputProps.ref}
      id={id || null}
      name={id || null}
      autoFocus={autoFocus || false}
      sx={[
        classes.outlined_inputs,
        {
          '&.Mui-focused': {
            border: `1px solid ${theme?.palette?.primary?.main}`,
          },
        },
        InputClassName,
        ...(disabledAddressBundle
          ? [
              {
                ...classes.outlined_input_bundle,
                backgroundColor: theme?.palette?.disabledAddress?.main,
                '& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled': {
                  color: theme?.palette?.disabled?.main,
                  WebkitTextFillColor: theme?.palette?.disabled?.main,
                },
              },
            ]
          : isBundle
          ? [
              classes.customBorderRadius,
              classes.outlined_input_bundle,
              showError && {
                backgroundColor: '#D5151F1A !important',
                // border: `1px solid ${theme?.palette?.danger?.main}`,
              },
            ]
          : []),
      ]}
      slotProps={{
        notchedOutline: {
          sx: [
            error ? classes.notchedOutlineErr : classes.notchedOutline,
            ...(disabledAddressBundle
              ? [
                  {
                    border: 'none',
                    '&.Mui-disabled': {
                      backgroundColor: '#E6E6E6',
                    },
                  },
                ]
              : isBundle
              ? [classes.customBorderRadius]
              : []),
          ],
        },
        input: {
          sx: {
            ...(isBundle && {
              padding: '14px 14px 10px 14px !important',
            }),
            '&:-webkit-autofill': {
              WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
              WebkitTextFillColor: !isLightTheme ? '#ffffff !important' : 'inherit !important',
              transition: 'background-color 5000s ease-in-out 0s',
              caretColor: 'inherit',
              borderRadius: 'inherit',
              fontSize: 'inherit',
            },
          },
        },
      }}
      placeholder={placeholder || ''}
      fullWidth={fullWidth || false}
      value={value || ''}
      disabled={bundleAddress && !isFullAddressEdited && value !== ''}
      style={error ? { background: COLOR4 } : {}}
      endAdornment={
        bundleAddress && (
          <InputAdornment position="end">
            <IconButton
              onClick={handleEditClick}
              disabled={isFullAddressEdited}
              aria-label="edit"
              edge="end"
            >
              <EditIcon color={theme?.palette?.orientalPrimary?.main} />
            </IconButton>
          </InputAdornment>
        )
      }
    />
  );

  return (
    <>
      <InputLabel
        data-testid={dataTestIdForLabel || null}
        htmlFor={id || null}
        required={(!bundleAddress && required) || false}
        aria-label={inputLabelAriaLabel || null}
        sx={[classes.label_title, InputLabelClassName]}
      >
        {label || ''}
      </InputLabel>
      <Autocomplete
        id={id || null}
        data-testid={dataTestIdAutocomplete || null}
        options={options}
        freeSolo
        value={value || ''}
        inputValue={value || ''}
        onChange={handleChange}
        onInputChange={handleInputChange}
        filterOptions={filterOptions}
        disablePortal
        slotProps={{
          popper: {
            strategy: 'fixed',
            placement: 'bottom-start',
            modifiers: [
              {
                name: 'flip',
                options: {
                  fallbackPlacements: ['top'],
                },
              },
              {
                name: 'computeStyles',
                options: {
                  adaptive: false,
                },
              },
            ],
          },
          paper: {
            sx: {
              backgroundColor: theme?.menuBackground,
              color: theme?.palette?.primary?.main,
            },
          },
        }}
        renderInput={(params) => {
          params.inputProps.maxLength = maxLength || null;
          return bundleAddress ? bundleInput(params) : oldInput(params);
        }}
      />
    </>
  );
};

CustomAutoComplete.propTypes = {
  required: PropTypes.bool,
  bundleAddress: PropTypes.bool,
  options: PropTypes.array,
  value: PropTypes.string,
  handleChange: PropTypes.func,
  filterOptions: PropTypes.func,
  maxLength: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  autoFocus: PropTypes.bool,
  error: PropTypes.string,
  InputClassName: PropTypes.string,
  id: PropTypes.string,
  inputLabelAriaLabel: PropTypes.string,
  InputLabelClassName: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  fullWidth: PropTypes.bool,
  isBundle: PropTypes.bool,
  dataTestIdAutocomplete: PropTypes.string,
  dataTestIdForInput: PropTypes.string,
  dataTestIdForLabel: PropTypes.string,
  handleInputChange: PropTypes.func,
  disabledAddressBundle: PropTypes.func,
};
